/**
 * ProductForm — minimal, keyboard-first form used inside both
 * "Add product" (modal) and "Edit product" (drawer) flows.
 */
import { notifySuccess } from '@/store/useToastStore';
import { useErrorPopup } from '@/hooks/useErrorPopup';
import { useEffect, useMemo, useState } from 'react';
import { Sparkles, Check, X } from 'lucide-react';
import { Product, Category } from '@/db/models/Inventory';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { createProduct, updateProduct, createCategory, type ProductInput } from '@/inventory/InventoryCrudService';
import { detectHSN, categoryForHSN, gstRatesForCode } from '@/utils/hsnDatabase';
import { useSettingsStore } from '@/store/useSettingsStore';
import { convertEntryMode, toStoredSellingPrice, toEnteredSellingPrice, priceBreakdown } from '@/billing/priceMode';
import { formatCurrency } from '@/utils/format';

/** Build a readable, reasonably-unique SKU from a product name. */
function generateSku(name: string): string {
  const slug = name.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 4) || 'SKU';
  const rand = Math.random().toString(36).slice(2, 6).toUpperCase();
  return `${slug}-${rand}`;
}

/** Suggest existing categories whose name overlaps the typed product name. */
function suggestCategories(name: string, categories: Category[]): Category[] {
  const tokens = name.toLowerCase().split(/[\s,\-/()]+/).filter((w) => w.length >= 3);
  if (tokens.length === 0) return [];
  return categories
    .filter((c) => {
      const cn = c.name.trim().toLowerCase();
      if (!cn) return false;
      return tokens.some((t) => cn.includes(t) || t.includes(cn));
    })
    .slice(0, 3);
}

const UNITS = ['PCS', 'NOS', 'KG', 'GM', 'LTR', 'ML', 'MTR', 'PKT', 'BOX', 'DOZ'];
const GST_SLABS = [0, 0.25, 3, 5, 12, 18, 28];

export interface ProductFormProps {
  storeId: string;
  merchantId: string;
  categories: Category[];
  /** When provided, the form edits this product; otherwise it creates a new one. */
  product?: Product;
  onSaved: (product: Product) => void;
  onCancel: () => void;
}

export function ProductForm({ storeId, merchantId, categories, product, onSaved, onCancel }: ProductFormProps) {
  const isEdit = !!product;
  // Categories created inline via a suggestion (kept locally so the <select>
  // shows them immediately, before the parent's reactive query catches up).
  const [extraCats, setExtraCats] = useState<Category[]>([]);
  const allCategories = useMemo(() => {
    const seen = new Set(categories.map((c) => c.id));
    return [...categories, ...extraCats.filter((c) => !seen.has(c.id))];
  }, [categories, extraCats]);

  // Price entry mode. The store's canonical billing convention is TAX_INCLUSIVE;
  // the per-entry checkbox lets the user type With- or Without-GST and we
  // normalise to canonical on save. Read once — these don't change mid-edit.
  const storeInclusive = useSettingsStore.getState().getBoolean('TAX_INCLUSIVE');
  const defaultInclGst = useSettingsStore.getState().getBoolean('PRODUCT_PRICE_INCLUDES_GST');

  const [name, setName] = useState(product?.name ?? '');
  const [categoryId, setCategoryId] = useState<string | null>(product?.categoryId ?? null);
  const [barcode, setBarcode] = useState(product?.barcode ?? '');
  const [sku, setSku] = useState(product?.sku ?? '');
  const [hsnCode, setHsnCode] = useState(product?.hsnCode ?? '');
  const [priceInclGst, setPriceInclGst] = useState(defaultInclGst);
  const [sellingPrice, setSellingPrice] = useState(
    product
      ? String(toEnteredSellingPrice(product.sellingPrice, product.gstRate, product.cessRate, defaultInclGst, storeInclusive))
      : '0',
  );
  const [mrp, setMrp] = useState(product ? String(product.mrp) : '0');
  const [purchasePrice, setPurchasePrice] = useState(product ? String(product.purchasePrice) : '0');
  const [currentStock, setCurrentStock] = useState(product ? String(product.currentStock) : '0');
  const [lowStockAlert, setLowStockAlert] = useState(product ? String(product.lowStockAlert) : '5');
  const [unit, setUnit] = useState(product?.unit ?? 'PCS');
  const [gstRate, setGstRate] = useState(product ? String(product.gstRate) : '0');
  const [trackInventory, setTrackInventory] = useState(product?.trackInventory ?? true);
  const [isService, setIsService] = useState(product?.isService ?? false);
  const [isActive, setIsActive] = useState(product?.isActive ?? true);

  const [error, setError] = useState<string | null>(null);
  useErrorPopup(error, () => setError(null));
  const [saving, setSaving] = useState(false);

  // ── Offline auto-suggest (HSN + GST + category) from the product name ──
  const [debouncedName, setDebouncedName] = useState(name);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedName(name), 250);
    return () => clearTimeout(t);
  }, [name]);
  const [hsnDismissed, setHsnDismissed] = useState(false);
  const [catDismissed, setCatDismissed] = useState(false);

  const hsnSuggestion = useMemo(() => detectHSN(debouncedName), [debouncedName]);
  const catSuggestions = useMemo(() => suggestCategories(debouncedName, allCategories), [debouncedName, allCategories]);
  const suggestedCatName = hsnSuggestion ? categoryForHSN(hsnSuggestion.code) : null;
  // Only offer HSN/GST while the HSN field is still blank — never override manual entry.
  const showHsnSuggest = !!hsnSuggestion && !hsnDismissed && !hsnCode.trim();
  const showCatSuggest = !categoryId && !catDismissed && catSuggestions.length > 0;

  /** Resolve a category by name, creating it if it doesn't exist yet. */
  async function resolveCategory(catName: string): Promise<void> {
    const existing = allCategories.find((c) => c.name.trim().toLowerCase() === catName.toLowerCase());
    if (existing) { setCategoryId(existing.id); return; }
    try {
      const created = await createCategory(merchantId, catName);
      setExtraCats((prev) => [...prev, created]);
      setCategoryId(created.id);
    } catch {
      /* leave uncategorized rather than fail the whole apply */
    }
  }

  /** Apply the full suggestion: HSN + GST, a category, and a SKU if blank. */
  async function applyHsnSuggestion() {
    if (!hsnSuggestion) return;
    setHsnCode(hsnSuggestion.code);
    setGstRate(String(hsnSuggestion.gstRate));
    if (!sku.trim()) setSku(generateSku(name));
    if (!categoryId && suggestedCatName) await resolveCategory(suggestedCatName);
  }

  function num(v: string) { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; }

  // Flip the entry mode, converting the field so the underlying price is stable.
  function setPriceMode(nextIncl: boolean) {
    if (nextIncl === priceInclGst) return;
    const converted = convertEntryMode(num(sellingPrice), priceInclGst, nextIncl, num(gstRate), 0);
    setSellingPrice(converted ? String(converted) : '0');
    setPriceInclGst(nextIncl);
  }

  // Live pre-tax / GST / customer-price split for the entered selling price.
  const priceSplit = priceBreakdown(num(sellingPrice), num(gstRate), 0, priceInclGst);

  // Non-blocking pricing sanity warnings
  const warnings: string[] = [];
  const sp = num(sellingPrice);
  const mrpN = num(mrp);
  const pp = num(purchasePrice);
  if (!isService && mrpN > 0 && sp > mrpN) warnings.push('Selling price is above MRP — selling above MRP is not permitted for retail goods.');
  if (pp > 0 && sp > 0 && sp < pp) warnings.push('Selling price is below purchase price — you will lose money on every sale.');
  if (num(gstRate) === 0) warnings.push('GST rate is 0% — confirm this item is genuinely nil-rated or exempt.');
  // HSN ↔ GST consistency: if a recognised HSN is entered, flag a GST rate that
  // doesn't match any rate we know for that code — guards against wrong tax.
  const cleanHsnLive = hsnCode.trim();
  const knownRates = /^\d{4}$|^\d{6}$|^\d{8}$/.test(cleanHsnLive) ? gstRatesForCode(cleanHsnLive) : [];
  if (knownRates.length > 0 && !knownRates.includes(num(gstRate))) {
    warnings.push(`HSN ${cleanHsnLive} is usually taxed at ${knownRates.map((r) => `${r}%`).join(' or ')} GST — you selected ${num(gstRate)}%. Please confirm.`);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) { setError('Name is required.'); return; }
    const cleanHsn = hsnCode.trim();
    if (!cleanHsn) {
      setError('HSN/SAC code is required for GST. Use the suggestion above or enter a 4, 6, or 8-digit code.');
      return;
    }
    if (!/^\d{4}$|^\d{6}$|^\d{8}$/.test(cleanHsn)) {
      setError('HSN/SAC code must be 4, 6, or 8 digits.');
      return;
    }
    setError(null);
    setSaving(true);
    try {
      const payload: ProductInput = {
        storeId,
        name: name.trim(),
        categoryId: categoryId || null,
        barcode: barcode.trim() || null,
        sku: sku.trim() || null,
        hsnCode: cleanHsn || null,
        // Normalise the entered price into the store's canonical convention.
        sellingPrice: toStoredSellingPrice(num(sellingPrice), num(gstRate), 0, priceInclGst, storeInclusive),
        mrp: num(mrp),
        purchasePrice: num(purchasePrice),
        currentStock: num(currentStock),
        lowStockAlert: Math.max(0, num(lowStockAlert)),
        unit,
        gstRate: num(gstRate),
        cessRate: 0,
        trackInventory: !isService && trackInventory,
        isService,
        isActive,
      };
      const saved = isEdit ? await updateProduct(product!, payload) : await createProduct(payload);
      notifySuccess(isEdit ? `“${saved.name}” updated.` : `“${saved.name}” added to inventory.`);
      onSaved(saved);
    } catch (err: any) {
      setError(err?.message || 'Failed to save product.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex max-h-[80vh] flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4 space-y-4">
        {/* Name */}
        <Field label="Name" required>
          <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </Field>

        {/* Category */}
        <Field label="Category">
          <select
            value={categoryId ?? ''}
            onChange={(e) => setCategoryId(e.target.value || null)}
            aria-label="Category"
            className="h-9 w-full rounded-md border border-stroke-hairline/20 bg-bg-surface px-3 text-body text-fg-primary"
          >
            <option value="">Uncategorized</option>
            {allCategories.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          {showCatSuggest && (
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
              <span className="inline-flex items-center gap-1 text-label uppercase text-accent-ink">
                <Sparkles size={11} /> Suggested
              </span>
              {catSuggestions.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setCategoryId(c.id)}
                  className="rounded-md border border-accent/30 bg-accent/5 px-2 py-0.5 text-label text-accent-ink hover:bg-accent/10 transition-colors"
                >
                  {c.name}
                </button>
              ))}
              <button type="button" onClick={() => setCatDismissed(true)} className="text-fg-tertiary hover:text-fg-secondary" aria-label="Dismiss suggestion">
                <X size={12} />
              </button>
            </div>
          )}
        </Field>

        {/* SKU + Barcode + HSN */}
        <div className="grid grid-cols-3 gap-3">
          <Field label="SKU"><Input value={sku} onChange={(e) => setSku(e.target.value)} /></Field>
          <Field label="Barcode"><Input value={barcode} onChange={(e) => setBarcode(e.target.value)} /></Field>
          <Field label="HSN / SAC" required>
            <Input value={hsnCode} onChange={(e) => setHsnCode(e.target.value)} inputMode="numeric" placeholder="4, 6, or 8 digits" />
          </Field>
        </div>

        {/* Offline HSN + GST auto-suggest from the product name */}
        {showHsnSuggest && hsnSuggestion && (
          <div className="flex items-center gap-3 rounded-md border border-accent/25 bg-accent/5 px-3 py-2">
            <Sparkles size={15} className="shrink-0 text-accent-ink" />
            <div className="min-w-0 flex-1 text-body">
              <span className="text-fg-primary">HSN {hsnSuggestion.code}</span>
              <span className="text-fg-tertiary"> · GST {hsnSuggestion.gstRate}%</span>
              {hsnSuggestion.cessRate > 0 && <span className="text-fg-tertiary"> · Cess {hsnSuggestion.cessRate}%</span>}
              {!categoryId && suggestedCatName && <span className="text-fg-tertiary"> · {suggestedCatName}</span>}
              <span className="ml-1 truncate text-label text-fg-tertiary">— {hsnSuggestion.description}</span>
            </div>
            <button
              type="button"
              onClick={() => void applyHsnSuggestion()}
              className="inline-flex shrink-0 items-center gap-1 rounded-md bg-accent px-2.5 py-1 text-label uppercase text-white hover:bg-accent/90 transition-colors"
            >
              <Check size={12} /> Apply
            </button>
            <button type="button" onClick={() => setHsnDismissed(true)} className="shrink-0 text-fg-tertiary hover:text-fg-secondary" aria-label="Dismiss suggestion">
              <X size={14} />
            </button>
          </div>
        )}

        {/* Price entry mode: With / Without GST */}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-label uppercase text-fg-tertiary">Selling price entered</span>
          <div className="inline-flex rounded-md border border-stroke-hairline/15 p-0.5">
            {[
              { incl: true, label: 'With GST' },
              { incl: false, label: 'Without GST' },
            ].map((opt) => (
              <button
                key={opt.label}
                type="button"
                onClick={() => setPriceMode(opt.incl)}
                className={
                  'rounded px-3 py-1 text-label transition-colors duration-120 ' +
                  (priceInclGst === opt.incl ? 'bg-accent/10 text-accent-ink' : 'text-fg-secondary hover:bg-bg-subtle')
                }
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Prices */}
        <div className="grid grid-cols-3 gap-3">
          <Field label={priceInclGst ? 'Selling price (incl. GST)' : 'Selling price (pre-tax)'} required>
            <Input value={sellingPrice} onChange={(e) => setSellingPrice(sanitizeNum(e.target.value))} inputMode="decimal" />
          </Field>
          <Field label="MRP">
            <Input value={mrp} onChange={(e) => setMrp(sanitizeNum(e.target.value))} inputMode="decimal" />
          </Field>
          <Field label="Purchase price">
            <Input value={purchasePrice} onChange={(e) => setPurchasePrice(sanitizeNum(e.target.value))} inputMode="decimal" />
          </Field>
        </div>

        {/* Live GST split so the user sees exactly what is stored vs charged */}
        {num(sellingPrice) > 0 && num(gstRate) > 0 && (
          <div className="rounded-md border border-stroke-hairline/10 bg-bg-subtle/40 px-3 py-2 text-label text-fg-secondary">
            Pre-tax {formatCurrency(priceSplit.preTax)} + GST {formatCurrency(priceSplit.tax)} ({num(gstRate)}%) ={' '}
            <span className="text-fg-primary">{formatCurrency(priceSplit.inclusive)}</span> customer pays.
            {!storeInclusive && priceInclGst && (
              <span className="text-fg-tertiary"> Stored pre-tax as {formatCurrency(priceSplit.preTax)}.</span>
            )}
            {storeInclusive && !priceInclGst && (
              <span className="text-fg-tertiary"> Stored inclusive as {formatCurrency(priceSplit.inclusive)}.</span>
            )}
          </div>
        )}

        {/* Stock + Low-stock alert + Unit */}
        <div className="grid grid-cols-3 gap-3">
          <Field label="Current stock">
            <Input
              value={currentStock}
              onChange={(e) => setCurrentStock(sanitizeNum(e.target.value))}
              disabled={isService || !trackInventory}
              inputMode="decimal"
            />
          </Field>
          <Field label="Low-stock alert">
            <Input
              value={lowStockAlert}
              onChange={(e) => setLowStockAlert(sanitizeNum(e.target.value))}
              disabled={isService || !trackInventory}
              inputMode="decimal"
            />
          </Field>
          <Field label="Unit">
            <select
              value={unit}
              onChange={(e) => setUnit(e.target.value)}
              aria-label="Unit"
              className="h-9 w-full rounded-md border border-stroke-hairline/20 bg-bg-surface px-3 text-body text-fg-primary"
            >
              {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
          </Field>
        </div>

        {/* GST */}
        <Field label="GST rate">
          <div className="flex flex-wrap gap-1.5">
            {GST_SLABS.map((slab) => (
              <button
                key={slab}
                type="button"
                onClick={() => setGstRate(String(slab))}
                className={
                  'rounded-md px-2.5 py-1 text-body border transition-colors duration-120 ease-apple ' +
                  (num(gstRate) === slab
                    ? 'border-accent/30 bg-accent/10 text-accent-ink'
                    : 'border-stroke-hairline/15 text-fg-secondary hover:bg-bg-subtle')
                }
              >
                {slab}%
              </button>
            ))}
          </div>
        </Field>

        {/* Toggles */}
        <div className="space-y-2 pt-1">
          <Toggle
            label="Service item"
            description="Skip inventory tracking entirely (e.g. consultations, repairs)."
            checked={isService}
            onChange={(v) => { setIsService(v); if (v) setTrackInventory(false); }}
          />
          <Toggle
            label="Track inventory"
            description="Decrements stock on each sale and warns at the low-stock threshold."
            checked={trackInventory}
            onChange={setTrackInventory}
            disabled={isService}
          />
          <Toggle
            label="Active"
            description="Inactive products are hidden from the billing catalog."
            checked={isActive}
            onChange={setIsActive}
          />
        </div>

        {warnings.map((w) => (
          <div key={w} className="rounded-md border border-warning/20 bg-warning/10 px-3 py-2 text-body text-warning-ink">{w}</div>
        ))}

      </div>

      <div className="flex items-center justify-end gap-2 border-t border-stroke-hairline/10 px-5 py-3">
        <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button type="submit" disabled={saving}>{saving ? 'Saving…' : isEdit ? 'Save changes' : 'Add product'}</Button>
      </div>
    </form>
  );
}

// ─────────────────────────────────────────────────────────────────────────

function sanitizeNum(value: string): string {
  const cleaned = value.replace(/^0+(?=\d)/, '');
  if (/^\d*\.?\d*$/.test(cleaned)) return cleaned;
  return value.slice(0, -1);
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-label uppercase text-fg-tertiary">
        {label}{required && <span className="ml-0.5 text-danger-ink">*</span>}
      </span>
      {children}
    </label>
  );
}

function Toggle({
  label,
  description,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked ? 'true' : 'false'}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className="flex w-full items-center justify-between gap-3 rounded-md px-2 py-2 text-left hover:bg-bg-subtle disabled:opacity-40 disabled:cursor-not-allowed"
    >
      <div>
        <div className="text-body text-fg-primary">{label}</div>
        {description && <div className="mt-0.5 text-label text-fg-tertiary normal-case">{description}</div>}
      </div>
      <span
        className={
          'relative inline-block h-5 w-9 shrink-0 rounded-full transition-colors duration-120 ease-apple ' +
          (checked ? 'bg-accent' : 'bg-ink-300')
        }
      >
        <span
          className={
            'absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white shadow-[0_1px_2px_rgba(11,18,32,0.35)] transition-transform duration-120 ease-apple ' +
            (checked ? 'translate-x-4' : 'translate-x-0')
          }
        />
      </span>
    </button>
  );
}
